import { Link } from "react-router-dom";
import { MapPin, Globe, Twitter } from "lucide-react";
import { optimizeGhostImage } from "@/lib/blog-utils";
import { useLocalizedPath } from "@/hooks/useLocalizedPath";

interface AuthorCardProps {
  author: {
    name: string;
    slug: string;
    profileImage?: string | null;
    bio?: string | null;
    location?: string | null;
    website?: string | null;
    twitter?: string | null;
    facebook?: string | null;
    postCount?: number;
  };
  variant?: "compact" | "full";
  linked?: boolean;
}

const AuthorCard = ({ author, variant = "compact", linked = true }: AuthorCardProps) => {
  const { getLocalizedPath } = useLocalizedPath();
  const content = (
    <div className={`flex ${variant === "full" ? "flex-col items-center text-center" : "items-center gap-3"}`}>
      {author.profileImage ? (
        <img
          src={optimizeGhostImage(author.profileImage, variant === "full" ? 128 : 48)}
          alt={author.name}
          className={`rounded-full object-cover ${variant === "full" ? "w-24 h-24 mb-4" : "w-10 h-10"}`}
        />
      ) : (
        <div
          className={`rounded-full bg-primary/10 flex items-center justify-center ${
            variant === "full" ? "w-24 h-24 mb-4" : "w-10 h-10"
          }`}
        >
          <span className={`font-semibold text-primary ${variant === "full" ? "text-2xl" : "text-sm"}`}>
            {author.name.charAt(0)}
          </span>
        </div>
      )}

      <div className={variant === "full" ? "" : "flex-1"}>
        <h3 className={`font-semibold text-foreground ${variant === "full" ? "text-xl mb-2" : "text-sm"}`}>
          {author.name}
        </h3>

        {variant === "full" && (
          <>
            {author.location && (
              <div className="flex items-center justify-center gap-1 text-muted-foreground text-sm mb-3">
                <MapPin className="w-4 h-4" />
                <span>{author.location}</span>
              </div>
            )}

            {author.bio && <p className="text-muted-foreground text-base max-w-lg mx-auto mb-4">{author.bio}</p>}

            <div className="flex items-center justify-center gap-4">
              {author.website && (
                <a
                  href={author.website}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-muted-foreground hover:text-primary transition-colors"
                  onClick={(e) => e.stopPropagation()}
                >
                  <Globe className="w-5 h-5" />
                </a>
              )}
              {author.twitter && (
                <a
                  href={`https://twitter.com/${author.twitter.replace("@", "")}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-muted-foreground hover:text-primary transition-colors"
                  onClick={(e) => e.stopPropagation()}
                >
                  <Twitter className="w-5 h-5" />
                </a>
              )}
            </div>

            {author.postCount !== undefined && (
              <p className="text-sm text-muted-foreground mt-4">
                {author.postCount} {author.postCount === 1 ? "article" : "articles"}
              </p>
            )}
          </>
        )}
      </div>
    </div>
  );

  if (linked) {
    return (
      <Link to={getLocalizedPath(`/blog/author/${author.slug}`)} className="block hover:opacity-80 transition-opacity">
        {content}
      </Link>
    );
  }

  return content;
};

export default AuthorCard;
